import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import { ExamByCodeFormData } from '../../../../hooks/useExamByCodeForm';

interface QuestionnaireUploadFormProps {
    touchedFields: Record<keyof ExamByCodeFormData, boolean>;
    onBlur: (field: keyof ExamByCodeFormData) => void;
    isSubmitting: boolean;
    formData: ExamByCodeFormData;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    disabled?: boolean;
    handleUploadQuestionnaireClick: () => void;
    fileQuestionnaireInputRef: React.RefObject<HTMLInputElement>;
    handleFileQuestionnaireChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    selectedQuestionnaireFile: File | null;
    handleUploadQuestionnaireKeysClick: () => void;
    fileQuestionnaireKeysInputRef: React.RefObject<HTMLInputElement>;
    handleFileQuestionnaireKeysChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    selectedQuestionnaireKeysFile: File | null | any;
}

const QuestionnaireUploadForm: React.FC<QuestionnaireUploadFormProps> = ({
    handleUploadQuestionnaireClick,
    fileQuestionnaireInputRef,
    handleFileQuestionnaireChange,
    selectedQuestionnaireFile,
    handleUploadQuestionnaireKeysClick,
    fileQuestionnaireKeysInputRef,
    handleFileQuestionnaireKeysChange,
    selectedQuestionnaireKeysFile
}) => {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* QUESTIONNAIRE UPLOAD */}
            <Stack spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={600}>
                    Questionnaire
                </Typography>
                <Box
                    onClick={handleUploadQuestionnaireClick}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        {selectedQuestionnaireFile ? (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={500}
                                        noWrap
                                        sx={{
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            maxWidth: 200
                                        }}
                                    >
                                        {selectedQuestionnaireFile.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Click to change questionnaire
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        Upload Questionnaire
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Browse file to upload
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Stack>

                    <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        ref={fileQuestionnaireInputRef}
                        onChange={handleFileQuestionnaireChange}
                        style={{ display: 'none' }}
                    />
                </Box>
            </Stack>

            {/* QUESTIONNAIRE WITH KEYS FORM */}
            <Stack spacing={2} sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight={600}>
                    Answer key
                </Typography>
                <Box
                    onClick={handleUploadQuestionnaireKeysClick}
                    sx={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f9f9f9',
                        },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        {selectedQuestionnaireKeysFile ? (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={500}
                                        noWrap
                                        sx={{
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            maxWidth: 200
                                        }}
                                    >
                                        {selectedQuestionnaireKeysFile.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Click to change answer key
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <>
                                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        Upload Answer key
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Browse file to upload
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Stack>

                    <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        ref={fileQuestionnaireKeysInputRef}
                        onChange={handleFileQuestionnaireKeysChange}
                        style={{ display: 'none' }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};


export default QuestionnaireUploadForm;
